// API Service for CRUD operations
import { API_URL } from "@/shared/config";

export interface ApiResponse<T> {
  status: string;
  fields?: T;
  created?: any[];
  failed?: any[];
  errors?: any;
}

export class ApiService {
  private static getHeaders(token: string) {
    return {
      "Content-Type": "application/json",
      Authorization: `token ${token}`,
    };
  }

  // ===== APPOINTMENTS =====
  static async getAppointments(
    token: string,
    buildingPk: number,
    startDate: string,
    endDate?: string
  ) {
    const url =
      endDate === startDate
        ? `${API_URL}/list/deleteGetPatchAppointment/${buildingPk}/?start=${startDate}`
        : `${API_URL}/list/deleteGetPatchAppointment/${buildingPk}/?start=${startDate}&end=${endDate}`;

    const response = await fetch(url, {
      method: "GET",
      headers: this.getHeaders(token),
    });

    if (!response.ok) throw new Error("Network response was not ok");
    return response.json();
  }

  static async createAppointment(token: string, data: any) {
    const response = await fetch(`${API_URL}/list/createAppointment/`, {
      method: "POST",
      headers: this.getHeaders(token),
      body: JSON.stringify(data),
    });

    if (!response.ok) throw new Error("Network response was not ok");
    return response.json();
  }

  static async updateAppointment(token: string, buildingPk: number, data: any) {
    const response = await fetch(
      `${API_URL}/list/deleteGetPatchAppointment/${buildingPk}/`,
      {
        method: "PATCH",
        headers: this.getHeaders(token),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) throw new Error("Network response was not ok");
    return response.json();
  }

  static async deleteAppointment(
    token: string,
    buildingPk: number,
    pk: number | string
  ) {
    const response = await fetch(
      `${API_URL}/list/deleteGetPatchAppointment/${buildingPk}/`,
      {
        method: "DELETE",
        headers: this.getHeaders(token),
        body: JSON.stringify({ pk }),
      }
    );

    if (!response.ok) throw new Error("Network response was not ok");
    return response.json();
  }

  // ===== RESERVATIONS/MEETINGS =====
  static async getReservations(
    token: string,
    buildingPk: number,
    startDate: string,
    endDate: string
  ) {
    const url = `${API_URL}/salle/deleteGetPatchReservation/${buildingPk}/?start=${startDate}&end=${endDate}`;

    const response = await fetch(url, {
      method: "GET",
      headers: this.getHeaders(token),
    });

    if (!response.ok) throw new Error("Network response was not ok");
    return response.json();
  }

  static async getRooms(token: string, buildingPk: number) {
    const response = await fetch(
      `${API_URL}/salle/deleteGetPatchSalle/${buildingPk}/`,
      {
        method: "GET",
        headers: this.getHeaders(token),
      }
    );

    if (!response.ok) throw new Error("Network response was not ok");
    return response.json();
  }

  static async getAvailableRooms(
    token: string,
    buildingPk: number,
    data: {
      dates: string[][];
      start: string;
      end: string;
      salleIds?: number[];
    }
  ) {
    const response = await fetch(
      `${API_URL}/salle/getSalleAvailable/${buildingPk}/`,
      {
        method: "POST",
        headers: this.getHeaders(token),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) throw new Error("Network response was not ok");
    return response.json();
  }

  static async getSmartAvailableRooms(
    token: string,
    buildingPk: number,
    data: {
      dates: string[][];
      start: string;
      end: string;
      excludeReservationId?: string | number; // ID of reservation being edited (to exclude from conflicts)
      includeReservedRooms?: number[]; // Room IDs to include even if reserved (for edit mode)
    }
  ) {
    const response = await fetch(
      `${API_URL}/salle/getSmartSalleAvailable/${buildingPk}/`,
      {
        method: "POST",
        headers: this.getHeaders(token),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) throw new Error("Network response was not ok");
    return response.json();
  }

  static async createReservation(token: string, data: any) {
    const response = await fetch(`${API_URL}/salle/createReservation/`, {
      method: "POST",
      headers: this.getHeaders(token),
      body: JSON.stringify(data),
    });

    if (!response.ok) throw new Error("Network response was not ok");
    return response.json();
  }

  static async updateReservation(token: string, buildingPk: number, data: any) {
    const response = await fetch(
      `${API_URL}/salle/deleteGetPatchReservation/${buildingPk}/`,
      {
        method: "PATCH",
        headers: this.getHeaders(token),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) throw new Error("Network response was not ok");
    return response.json();
  }

  static async deleteReservation(
    token: string,
    buildingPk: number,
    pk: number | string
  ) {
    const response = await fetch(
      `${API_URL}/salle/deleteGetPatchReservation/${buildingPk}/`,
      {
        method: "DELETE",
        headers: this.getHeaders(token),
        body: JSON.stringify({ pk }),
      }
    );

    if (!response.ok) throw new Error("Network response was not ok");
    return response.json();
  }

  // Generic HTTP methods for payment operations
  static async get<T>(url: string, token?: string, init?: RequestInit): Promise<T> {
    const headers = token ? ApiService.getHeaders(token) : { "Content-Type": "application/json" };
    const { headers: initHeaders, ...restInit } = init ?? {};
    const response = await fetch(url, {
      method: "GET",
      headers: { ...headers, ...(initHeaders as Record<string, string> | undefined) },
      cache: init?.cache ?? "default",
      ...restInit,
    });

    const text = await response.text();
    let parsed: unknown = undefined;
    try {
      parsed = text ? JSON.parse(text) : undefined;
    } catch {
      parsed = text || undefined;
    }
    if (!response.ok) {
      const err = new Error("API request failed") as Error & {
        httpStatus?: number;
        data?: unknown;
      };
      err.httpStatus = response.status;
      err.data = parsed;
      throw err;
    }
    return parsed as T;
  }

  static async post<T>(url: string, data: any, token?: string): Promise<T> {
    const headers = token ? ApiService.getHeaders(token) : { "Content-Type": "application/json" };
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    const text = await response.text();
    let parsed: unknown = undefined;
    try {
      parsed = text ? JSON.parse(text) : undefined;
    } catch {
      parsed = text || undefined;
    }
    if (!response.ok) {
      const err = new Error("API request failed") as Error & {
        httpStatus?: number;
        data?: unknown;
      };
      err.httpStatus = response.status;
      err.data = parsed;
      throw err;
    }
    return parsed as T;
  }

  static async put<T>(url: string, data: any, token?: string): Promise<T> {
    const headers = token ? ApiService.getHeaders(token) : { "Content-Type": "application/json" };
    const response = await fetch(url, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) throw new Error("Network response was not ok");
    return response.json();
  }

  static async patch<T>(url: string, data: any, token: string): Promise<T> {
    const response = await fetch(url, {
      method: "PATCH",
      headers: ApiService.getHeaders(token),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const text = await response.text();
      let parsed: any = undefined;
      try {
        parsed = text ? JSON.parse(text) : undefined;
      } catch {
        parsed = text || undefined;
      }
      const err: any = new Error("API request failed");
      err.httpStatus = response.status;
      err.data = parsed;
      throw err;
    }
    return response.json();
  }

  static async delete<T = void>(url: string, token: string, data?: any): Promise<T> {
    const response = await fetch(url, {
      method: "DELETE",
      headers: ApiService.getHeaders(token),
      ...(data ? { body: JSON.stringify(data) } : {}),
    });

    const text = await response.text();
    let parsed: unknown = undefined;
    try {
      parsed = text ? JSON.parse(text) : undefined;
    } catch {
      parsed = text || undefined;
    }
    if (!response.ok) {
      const err = new Error("API request failed") as Error & {
        httpStatus?: number;
        data?: unknown;
      };
      err.httpStatus = response.status;
      err.data = parsed;
      throw err;
    }
    return parsed as T;
  }
}

// Export a default instance for convenience
export const apiService = {
  get: ApiService.get,
  post: ApiService.post,
  put: ApiService.put,
  patch: ApiService.patch,
  delete: <T = void>(url: string, token: string, data?: any) => ApiService.delete<T>(url, token, data),
};
